import React, { useEffect, useState } from 'react';
import { Row, Col } from 'react-bootstrap';
import { Link, useSearchParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus } from '@fortawesome/free-solid-svg-icons';
import { useQuery } from 'react-query';
import PageBreadcrumb from 'components/common/PageBreadcrumb';
import ProjectsTopSection from 'components/modules/project-management/ProjectsTopSection';
import CardViewItem from 'components/modules/project-management/card-view/CardViewItem';
import CardViewModal from 'components/modules/project-management/card-view/CardViewModal';
import { homepageBreadcrumbItems } from 'data/commonData';
import {
  GET_JOBS_QUERY,
  JobWithMetadata,
  GET_JOB_QUERY,
  JobComment
} from 'services/api/job';
import { GET_JOBSTAGE_QUERY } from 'services/api/jobStage';
import { GET_FILTERED_TASKS } from 'services/api/task';
import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import { JobStageFilter } from 'components/modules/project-management/JobStageFilter';
import { useJobs } from 'providers/JobContext';
import { calculateDaysReturned } from 'helpers/jobUtils';
import { usePermissions } from 'hooks/usePermissions';
import { PERMISSIONS } from 'constants/permissions';
import LoadingPlaceholder from 'components/loading-placeholder/LoadingPlaceholder';

const ProjectCardView = () => {
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [searchParams, setSearchParams] = useSearchParams();
  const [showJobModal, setShowJobModal] = useState<boolean>(false);
  const [selectedJob, setSelectedJob] = useState<JobWithMetadata | null>(null);
  const [modalComments, setModalComments] = useState<JobComment[]>([]);
  const jobId = searchParams.get('jobId');

  const {
    data: jobs,
    isLoading: isJobsLoading,
    isSuccess
  } = useQuery('getJobs', GET_JOBS_QUERY, {
    staleTime: 0
  });

  const [
    canViewNewJob,
    canViewEquipShipped,
    canViewEquipReturned,
    canViewOperatorDispatched,
    canViewOperatorReturned,
    canViewJobStaged,
    canUpdateJobStage,
    canUpdateMarkClosed,
    canUpdateJob,
    canAddNewProject,
    canAddCommentsField,
    canAddShopCommentsField
  ] = usePermissions([
    PERMISSIONS.DASHBOARD.ACTIVE_JOBS.JOBS_STAGES.NEW_JOB,
    PERMISSIONS.DASHBOARD.ACTIVE_JOBS.JOBS_STAGES.EQUIP_SHIPPED,
    PERMISSIONS.DASHBOARD.ACTIVE_JOBS.JOBS_STAGES.EQUIP_RETURNED,
    PERMISSIONS.DASHBOARD.ACTIVE_JOBS.JOBS_STAGES.OPERATOR_DISPATCHED,
    PERMISSIONS.DASHBOARD.ACTIVE_JOBS.JOBS_STAGES.OPERATOR_RETURNED,
    PERMISSIONS.DASHBOARD.ACTIVE_JOBS.JOBS_STAGES.JOB_STAGED,
    PERMISSIONS.DASHBOARD.ACTIVE_JOBS.UPDATE_JOB_STAGE,
    PERMISSIONS.DASHBOARD.ACTIVE_JOBS.MARK_JOB_CLOSED,
    PERMISSIONS.DASHBOARD.ACTIVE_JOBS.EDIT_JOB,
    PERMISSIONS.DASHBOARD.ACTIVE_JOBS.ADD_NEW_PROJECT,
    PERMISSIONS.DASHBOARD.ACTIVE_JOBS.ADD_COMMENTS_FIELD,
    PERMISSIONS.DASHBOARD.ACTIVE_JOBS.ADD_SHOP_COMMENTS_FIELD,
    PERMISSIONS.DASHBOARD.ACTIVE_JOBS.ADD_SHOP_COMMENTS_FIELD
  ]);

  const { data: JobStages, isFetched: isStagesFetched } = useQuery(
    'getJobStages',
    GET_JOBSTAGE_QUERY,
    {
      staleTime: 3 * 60 * 60 * 1000
    }
  );

  const { data: specificJobResponse, isLoading: isSpecificJobLoading } =
    useQuery(['specificJob', jobId], () => GET_JOB_QUERY(jobId as string), {
      enabled: !!jobId,
      staleTime: 3 * 60 * 60 * 1000,
      onError: (error: any) => {
        console.error('Failed to fetch specific job:', error);
        setSearchParams(params => {
          params.delete('jobId');
          return params;
        });
      }
    });

  const { data: jobTasks, isLoading: isLoadingTasks } = useQuery(
    ['jobTasks', jobId],
    () => GET_FILTERED_TASKS({ jobId: jobId as string }),
    {
      enabled: !!jobId && showJobModal,
      staleTime: 60 * 1000
    }
  );

  const { selectedStage, groupedJobs, setInitialJobs } = useJobs();

  useEffect(() => {
    if (isSuccess) {
      const enrichedJobs = jobs?.data?.map(job => {
        return {
          ...job,
          daysReturned: calculateDaysReturned(job),
          isPriority: job.isPriority
        };
      }) as JobWithMetadata[];
      setInitialJobs(enrichedJobs);
    }
  }, [jobs?.data]);

  // Handle email link with jobId parameter
  useEffect(() => {
    if (jobId && specificJobResponse?.data && !isSpecificJobLoading) {
      const jobData = specificJobResponse?.data;
      const transformedJob: JobWithMetadata = {
        ...jobData,
        daysReturned: calculateDaysReturned(jobData),
        isPriority: jobData.isPriority || false
      };

      setSelectedJob(transformedJob);
      setShowJobModal(true);

      if (jobData.comments) {
        const sortedComments = jobData.comments
          .slice()
          .sort(
            (a: JobComment, b: JobComment) =>
              new Date(b.createdAt || 0).getTime() -
              new Date(a.createdAt || 0).getTime()
          );
        setModalComments(sortedComments);
      }
    }
  }, [jobId, specificJobResponse, isSpecificJobLoading]);

  const handleModalClose = () => {
    setShowJobModal(false);
    setSelectedJob(null);
    setModalComments([]);
    setSearchParams(params => {
      params.delete('jobId');
      return params;
    });
  };

  const filterJobs = (jobs: any[], term: string) => {
    if (!term) return jobs;

    const searchLower = term.toLowerCase();
    return jobs?.filter(job => {
      return (
        job.title?.toLowerCase().includes(searchLower) ||
        job.jobNumber?.toLowerCase().includes(searchLower) ||
        job.well?.toLowerCase().includes(searchLower) ||
        job.description?.toLowerCase().includes(searchLower) ||
        job.customer?.organizationName?.toLowerCase().includes(searchLower) ||
        job.rig?.name?.toLowerCase().includes(searchLower) ||
        job.location?.location?.toLowerCase().includes(searchLower)
      );
    });
  };

  const handleSearch = (searchValue: string) => {
    setSearchTerm(searchValue);
  };

  const filteredGroupedJobs = Object?.fromEntries(
    Object?.entries(groupedJobs)?.map(([stage, stageJobs]) => [
      stage,
      filterJobs(stageJobs, searchTerm)
    ])
  );

  const permissionFilteredJobs = { ...filteredGroupedJobs };

  if (!canViewNewJob && permissionFilteredJobs['new job']) {
    delete permissionFilteredJobs['new job'];
  }

  if (!canViewEquipShipped && permissionFilteredJobs['equip shipped']) {
    delete permissionFilteredJobs['equip shipped'];
  }

  if (!canViewEquipReturned && permissionFilteredJobs['equip returned']) {
    delete permissionFilteredJobs['equip returned'];
  }

  if (
    !canViewOperatorDispatched &&
    permissionFilteredJobs['operator dispatched']
  ) {
    delete permissionFilteredJobs['operator dispatched'];
  }

  if (!canViewOperatorReturned && permissionFilteredJobs['operator returned']) {
    delete permissionFilteredJobs['operator returned'];
  }

  if (!canViewJobStaged && permissionFilteredJobs['job staged']) {
    delete permissionFilteredJobs['job staged'];
  }

  const displayJobs = selectedStage
    ? {
        [selectedStage.toLowerCase()]:
          permissionFilteredJobs[selectedStage.toLowerCase()] || []
      }
    : permissionFilteredJobs;

  const permissionFilteredTotalJobs = Object.entries(groupedJobs)
    .filter(([stage]) => {
      if (stage === 'new job' && !canViewNewJob) return false;
      if (stage === 'equip shipped' && !canViewEquipShipped) return false;
      if (stage === 'equip returned' && !canViewEquipReturned) return false;
      if (stage === 'operator dispatched' && !canViewOperatorDispatched)
        return false;
      if (stage === 'operator returned' && !canViewOperatorReturned)
        return false;
      if (stage === 'job staged' && !canViewJobStaged) return false;
      return true;
    })
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    .flatMap(([_, jobs]) => jobs);

  const totalJobsCount = permissionFilteredTotalJobs.length;
  const filteredJobsCount = Object.values(displayJobs).flat().length;

  if (isJobsLoading || isSpecificJobLoading) return <LoadingPlaceholder />;

  return (
    <div>
      <PageBreadcrumb items={homepageBreadcrumbItems} />
      <AdvanceTableProvider>
        {/* Header Section */}
        <div className="d-flex flex-wrap justify-content-between align-items-center mb-4">
          <h2 className="mb-0">
            <span className="me-3">Jobs</span>
            <span className="fw-normal text-700">
              {searchTerm
                ? `(${filteredJobsCount} of ${totalJobsCount})`
                : selectedStage
                ? `(${
                    displayJobs[selectedStage.toLowerCase()]?.length || 0
                  } of ${totalJobsCount})`
                : `(${totalJobsCount})`}
            </span>
          </h2>

          <div className="d-flex flex-row gap-2 mt-3 mt-sm-0">
            <JobStageFilter stages={JobStages?.data || []} />
            {canAddNewProject && (
              <Link
                className="btn btn-primary btn-sm px-3 py-2"
                to="/home/apps/job/create"
              >
                <FontAwesomeIcon icon={faPlus} className="me-2 me-sm-0" />
                Add new job
              </Link>
            )}
          </div>
        </div>

        <ProjectsTopSection activeView="card" onSearch={handleSearch} />

        {/* Content Section */}
        <div className="mt-4">
          {Object.values(displayJobs).flat().length === 0 ? (
            <div className="text-center py-5">
              <h3 className="text-700">No results found</h3>
              <p className="text-600">
                Try adjusting your search to find what you're looking for.
              </p>
            </div>
          ) : (
            Object?.keys(displayJobs)?.map(stageName => {
              const stageJobs = displayJobs[stageName];
              if (!stageJobs?.length) return null;

              return (
                <div key={stageName} className="mb-6">
                  <div className="d-flex align-items-center mb-3">
                    <h3 className="text-capitalize mb-0">{stageName}</h3>
                    <span className="fs-9 fw-normal text-700 ms-2">
                      ({stageJobs?.length})
                    </span>
                  </div>
                  <Row className="g-3">
                    {stageJobs?.map(project => (
                      <Col xs={12} sm={6} xl={4} xxl={3} key={project.id}>
                        <CardViewItem
                          project={project}
                          JobStages={JobStages?.data || []}
                          getJobStage={isStagesFetched}
                          canUpdateJobStage={canUpdateJobStage}
                          canUpdateMarkClosed={canUpdateMarkClosed}
                          canUpdateJob={canUpdateJob}
                          canAddCommentsField={canAddCommentsField}
                          canAddShopCommentsField={canAddShopCommentsField}
                          canAddJobStageCommentsField={canAddShopCommentsField}
                        />
                      </Col>
                    ))}
                  </Row>
                </div>
              );
            })
          )}
        </div>
      </AdvanceTableProvider>

      {/* Job Modal for email links */}
      {selectedJob && (
        <CardViewModal
          show={showJobModal}
          handleClose={handleModalClose}
          project={selectedJob}
          canEditJob={canUpdateJob}
          canAddCommentsField={canAddCommentsField}
          canAddShopCommentsField={canAddShopCommentsField}
          canAddJobStageCommentsField={canAddShopCommentsField}
          isLoadingTasks={isLoadingTasks}
          jobTasks={jobTasks?.data || []}
          comments={modalComments}
          setComments={setModalComments}
          scrollToTasks={false}
          setScrollToTasks={() => {
            console.log('scroll to tasks');
          }}
        />
      )}
    </div>
  );
};

export default ProjectCardView;
